import { useEffect, useState, useRef } from 'react'
import {
  Box,
  Button,
  Flex,
  Text,
  InlineMenu,
  Toggle,
} from '@pulsex/uikit'
import { styled } from 'styled-components'
import { useTranslation } from '@pulsex/localization'

interface FarmTypesFilterProps {
  v1FarmOnly?: boolean
  handleSetV1FarmOnly?: (value: boolean) => void
  v2FarmOnly?: boolean
  handleSetV2FarmOnly?: (value: boolean) => void
  farmTypesEnableCount: number
  handleSetFarmTypesEnableCount: (value: number) => void
}

export const FarmTypesWrapper = styled(Flex)`
  background: ${({ theme }) => theme.colors.dropdown};
  border-radius: 24px 24px 0 0;
`

export const StyledItemRow = styled(Flex)`
  cursor: pointer;
  user-select: none;
`

const ToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;

  ${Text} {
    margin-left: 8px;
  }
`

export const FarmTypesFilter: React.FC<FarmTypesFilterProps> = ({
  v1FarmOnly,
  handleSetV1FarmOnly,
  v2FarmOnly,
  handleSetV2FarmOnly,
  farmTypesEnableCount,
  handleSetFarmTypesEnableCount,
}) => {
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const handleMenuClick = () => setIsOpen(!isOpen)

  useEffect(() => {
    const handleClickOutside = ({ target }: Event) => {
      if (
        wrapperRef.current &&
        menuRef.current &&
        !menuRef.current.contains(target as HTMLElement) &&
        !wrapperRef.current.contains(target as HTMLElement)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [setIsOpen, wrapperRef, menuRef])

  return (
    <>
      <Flex alignItems="center" pl="8px">
        <Box ref={wrapperRef}>
          <InlineMenu
            component={
              <Button onClick={handleMenuClick} variant="light" scale="sm">
                {t('Farm Types')}
                {farmTypesEnableCount > 0 && `(${farmTypesEnableCount})`}
              </Button>
            }
            isOpen={isOpen}
            options={{ placement: 'top' }}
          >
            <Box width={['100%', '345px']} ref={menuRef}>
              <FarmTypesWrapper alignItems="center" p="16px">
                <Text fontSize="20px" bold color="text" display="inline-block" ml="8px">
                  {t('Farm Types')}
                </Text>
              </FarmTypesWrapper>
              <Box height="140px" overflowY="auto">
                <StyledItemRow alignItems="center" px="16px" py="8px" ml="8px" mt="8px">
                  <Text fontSize="16px" ml="10px" style={{ flex: 1 }} bold>
                    V1 {t('Farms')}
                  </Text>
                  <ToggleWrapper>
                    <Toggle
                      id="v1-only-farms"
                      checked={v1FarmOnly}
                      onChange={() => {
                        const totalFarmsEnableCount = farmTypesEnableCount + (!v1FarmOnly ? 1 : -1)
                        handleSetFarmTypesEnableCount(totalFarmsEnableCount)

                        handleSetV1FarmOnly?.(!v1FarmOnly)
                      }}
                      scale="sm"
                    />
                  </ToggleWrapper>
                </StyledItemRow>
                <StyledItemRow alignItems="center" px="16px" py="8px" ml="8px" mt="8px">
                  <Text fontSize="16px" ml="10px" style={{ flex: 1 }} bold>
                    V2 {t('Farms')}
                  </Text>
                  <ToggleWrapper>
                    <Toggle
                      id="v2-only-farms"
                      checked={v2FarmOnly}
                      onChange={() => {
                        const totalFarmsEnableCount = farmTypesEnableCount + (!v2FarmOnly ? 1 : -1)
                        handleSetFarmTypesEnableCount(totalFarmsEnableCount)

                        handleSetV2FarmOnly?.(!v2FarmOnly)
                      }}
                      scale="sm"
                    />
                  </ToggleWrapper>
                </StyledItemRow>
              </Box>
            </Box>
          </InlineMenu>
        </Box>
      </Flex>
    </>
  )
}
